import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import Loading from "../Components/Loading";
import { useNavigate } from "react-router-dom";

import { API_URL } from "../config/api";

const sortFieldMap = {
  assetName: "assetName",
  brand: "brand",
  description: "description",
  purchaseDate: "purchaseDate",
  warrantyExpiry: "warrantyExpiry",
  status: "status",
  id: "_id",
  createdAt: "createdAt",
  updatedAt: "updatedAt",
};

/** Flatten API rows like { assets: [...] } into one row per asset */
const normalizeAssetsList = (data) => {
  if (!Array.isArray(data)) return [];

  const flat = [];
  data.forEach((item) => {
    if (!item) return;

    const nestedAssets = item.assets;

    if (Array.isArray(nestedAssets) && nestedAssets.length > 0) {
      nestedAssets.forEach((asset, index) => {
        flat.push({
          ...asset,
          id:
            asset?.id || item?.id || `${asset?.assetName || "asset"}-${index}`,
        });
      });
      return;
    }

    if (item.assetName) {
      flat.push(item);
    }
  });

  return flat;
};

const getDescription = (asset) =>
  asset?.Description ?? asset?.description ?? "-";

const StoreAssets = () => {
  const navigate = useNavigate();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 25,
    hasNextPage: false,
    hasPreviousPage: false,
  });
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [assetToDelete, setAssetToDelete] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [showActionModal, setShowActionModal] = useState(false);
  const searchTimeoutRef = useRef(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedHistory, setSelectedHistory] = useState([]);
  const [selectedAssetName, setSelectedAssetName] = useState("");

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (search === "" || search.trim().length >= 2) {
      searchTimeoutRef.current = setTimeout(() => {
        setDebouncedSearch(search);
      }, 500);
    } else {
      setDebouncedSearch("");
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [search]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, [debouncedSearch]);

  useEffect(() => {
    fetchAssets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pagination.currentPage,
    pagination.limit,
    debouncedSearch,
    sortBy,
    sortOrder,
  ]);

  const fetchAssets = async () => {
    if (!API_URL) {
      toast.error(
        "API URL is not configured. Please set REACT_APP_API_URL in environment variables.",
      );
      return;
    }

    setLoading(true);
    try {
      const mappedSortBy = sortFieldMap[sortBy] || "createdAt";
      const payload = {
        action: "get-asset",
        search: debouncedSearch,
        page: pagination.currentPage,
        limit: pagination.limit,
        sortBy: mappedSortBy,
        sortOrder: sortOrder,
      };

      const response = await axios.post(API_URL, payload, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      const result = response.data;

      if (result?.success) {
        const rawList = result.assets || result.data || [];
        setAssets(normalizeAssetsList(rawList));
        setPagination((prev) => ({
          ...prev,
          currentPage: result.pagination?.currentPage || prev.currentPage,
          totalPages: result.pagination?.totalPages || prev.totalPages,
          totalCount: result.pagination?.totalCount || prev.totalCount,
          hasNextPage: result.pagination?.hasNextPage || false,
          hasPreviousPage: result.pagination?.hasPreviousPage || false,
        }));
      } else {
        toast.error(result?.message || "Failed to fetch assets");
      }
    } catch (error) {
      console.error("Error fetching assets:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Something went wrong. Please try again.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      const day = date.getDate();
      const month = date.toLocaleString("default", { month: "short" });
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch (error) {
      return dateString;
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    if (!API_URL) return;

    try {
      const payload = {
        action: "get-active-employees-list",
      };

      const response = await axios.post(API_URL, payload, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      const result = response.data;

      if (result?.success) {
        setEmployees(result.employees || []);
      }
    } catch (error) {
      console.error("Error fetching employees:", error);
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedAssets(assets.map((asset) => asset.id));
    } else {
      setSelectedAssets([]);
    }
  };

  const handleSelectAsset = (id) => {
    setSelectedAssets((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const handleAssignAssets = async () => {
    if (selectedAssets.length === 0) {
      toast.error("Please select at least one asset");
      return;
    }

    if (!selectedEmployee) {
      toast.error("Please select employee");
      return;
    }

    try {
      setLoading(true);

      const payload = {
        action: "allocate-assets",
        employeeId: selectedEmployee,
        assetIds: selectedAssets,
        updatedBy: localStorage.getItem("id"),
      };

      const response = await axios.post(API_URL, payload, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      const result = response.data;

      if (result?.success) {
        toast.success(result?.message || "Assets assigned successfully");

        setSelectedAssets([]);
        setSelectedEmployee("");
        setShowActionModal(false);

        fetchAssets();
      } else {
        toast.error(result?.message || "Failed to assign assets");
      }
    } catch (error) {
      console.error("Error assigning assets:", error);

      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Something went wrong",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleHistory = (asset) => {
    setSelectedAssetName(asset.assetName || "-");
    setSelectedHistory(asset.assetHistory || []);
    setShowHistoryModal(true);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";

    try {
      const date = new Date(dateString);

      return date.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });
    } catch (error) {
      return "-";
    }
  };

  const handleUnallocateAssets = async () => {
    if (selectedAssets.length === 0) {
      toast.error("Please select at least one asset");
      return;
    }

    if (!selectedEmployee) {
      toast.error("Please select employee");
      return;
    }

    try {
      setLoading(true);

      const payload = {
        action: "unallocate-assets",
        employeeId: selectedEmployee,
        assetIds: selectedAssets,
        updatedBy: localStorage.getItem("id"),
      };

      const response = await axios.post(API_URL, payload, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      const result = response.data;

      if (result?.success) {
        toast.success(result?.message || "Assets unallocated successfully");

        setSelectedAssets([]);
        setSelectedEmployee("");
        setShowActionModal(false);

        fetchAssets();
      } else {
        toast.error(result?.message || "Failed to unallocate assets");
      }
    } catch (error) {
      console.error("Error unallocating assets:", error);

      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Something went wrong",
      );
    } finally {
      setLoading(false);
    }
  };

  const getAssetName = (asset) => asset?.assetName || "-";

  const getAssetStatus = (asset) => asset?.status || "-";

  const getStatusClass = (status) => {
    if (!status || status === "-") return "active";
    return String(status).toLowerCase().replace(/\s+/g, "-");
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination((prev) => ({ ...prev, currentPage: newPage }));
    }
  };

  const handleLimitChange = (e) => {
    const newLimit = parseInt(e.target.value);
    setPagination((prev) => ({ ...prev, limit: newLimit, currentPage: 1 }));
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearch(value);
  };

  const handleSort = (field) => {
    let newSortOrder = "asc";

    if (sortBy === field) {
      newSortOrder = sortOrder === "asc" ? "desc" : "asc";
    }

    setSortBy(field);
    setSortOrder(newSortOrder);

    setPagination((prev) => ({
      ...prev,
      currentPage: 1,
    }));
  };

  const getSerialNumber = (index) => {
    return (pagination.currentPage - 1) * pagination.limit + index + 1;
  };

  const handleAdd = () => {
    navigate("/store-assets-add");
  };

  const handleView = (asset) => {
    if (asset?.id) {
      navigate(`/asset-view/${asset.id}`);
    } else {
      toast.error("Unable to open asset. Missing identifier.");
    }
  };

  const handleEdit = (asset) => {
    if (asset?.id) {
      navigate(`/store-assets-edit/${asset.id}`, { state: { asset } });
    } else {
      toast.error("Unable to open asset. Missing identifier.");
    }
  };

  const handleDelete = (asset) => {
    setAssetToDelete(asset);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setAssetToDelete(null);
  };

  const confirmDelete = async () => {
    if (!assetToDelete) return;

    const updatedBy = localStorage.getItem("id");

    if (!updatedBy) {
      toast.error("User ID not found. Please login again.");
      closeDeleteModal();
      return;
    }

    setLoading(true);

    try {
      const payload = {
        action: "delete-asset",
        id: assetToDelete.id,
        updatedBy: updatedBy,
      };

      const response = await axios.post(API_URL, payload, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      const result = response.data;

      if (result?.success) {
        toast.success(result?.message || "Asset deleted successfully");

        setAssets((prevAssets) =>
          prevAssets.filter((item) => item.id !== assetToDelete.id),
        );

        setPagination((prev) => ({
          ...prev,
          totalCount: prev.totalCount - 1,
        }));

        closeDeleteModal();
      } else {
        toast.error(result?.message || "Failed to delete asset");
      }
    } catch (error) {
      console.error("Error deleting asset:", error);

      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Something went wrong. Please try again.";

      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="employee-form">
      <section className="content">
        <div className="container-fluid">
          <div className="row mb-3 employee-header-row">
            <div className="col-md-8">
              <h1 className="employee-title">
                Manage Assets
                <i className="fas fa-info-circle ml-2 employee-title-icon"></i>
              </h1>
              <p className="employee-description">
                View, edit, and manage company assets and assignments.
              </p>
            </div>
            <div className="col-md-4 employee-view-header-actions">
              <button
                type="button"
                className="employee-back-btn"
                onClick={handleAdd}
              >
                Add Asset
              </button>
            </div>
          </div>

          <div className="card employee-content-card">
            <div className="card-body">
              <div className="employee-table-container">
                <div
                  className="employee-search-container"
                  style={{
                    display: "flex",
                    alignItems: "flex-end",
                    gap: "15px",
                    flexWrap: "wrap",
                  }}
                >
                  <div
                    className="form-group"
                    style={{ minWidth: "350px", width: "350px" }}
                  >
                    <input
                      type="text"
                      className="form-input employee-search-input"
                      placeholder="Search assets..."
                      value={search}
                      onChange={handleSearch}
                      style={{ height: "38px", width: "100%" }}
                    />
                  </div>

                  <div
                    className="form-group"
                    style={{ minWidth: "220px", width: "220px" }}
                  >
                    <label
                      className="form-label"
                      style={{
                        marginBottom: "5px",
                        fontSize: "14px",
                        fontWeight: "500",
                      }}
                    >
                      Employee
                    </label>

                    <select
                      className="form-control"
                      value={selectedEmployee}
                      onChange={(e) => setSelectedEmployee(e.target.value)}
                      style={{ height: "38px" }}
                    >
                      <option value="">Select Employee</option>

                      {employees.map((emp) => (
                        <option key={emp.userId} value={emp.userId}>
                          {emp.fullName || "-"}
                        </option>
                      ))}
                    </select>
                  </div>

                  <button
                    type="button"
                    className="employee-back-btn"
                    onClick={() => {
                      if (selectedAssets.length === 0) {
                        toast.error("Please select at least one asset");
                        return;
                      }

                      if (!selectedEmployee) {
                        toast.error("Please select employee");
                        return;
                      }

                      setShowActionModal(true);
                    }}
                    disabled={loading}
                    style={{
                      height: "38px",
                      minWidth: "170px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                    }}
                  >
                    Allocate / Unallocate
                  </button>
                </div>

                {loading ? (
                  <Loading size={120} />
                ) : (
                  <>
                    <div className="employee-table-wrapper">
                      <table className="employee-table">
                        <thead>
                          <tr>
                            <th>
                              <input
                                type="checkbox"
                                checked={
                                  assets.length > 0 &&
                                  selectedAssets.length === assets.length
                                }
                                onChange={handleSelectAll}
                              />
                            </th>

                            <th className="employee-sortable">SI No.</th>

                            <th
                              className="employee-sortable"
                              onClick={() => handleSort("assetName")}
                            >
                              Asset Name
                              {sortBy === "assetName" && (
                                <i
                                  className={`fas fa-sort-${sortOrder === "asc" ? "up" : "down"} ml-1`}
                                ></i>
                              )}
                              {sortBy !== "assetName" && (
                                <i className="fas fa-sort ml-1 employee-sort-icon"></i>
                              )}
                            </th>

                            <th
                              className="employee-sortable"
                              onClick={() => handleSort("brand")}
                            >
                              Brand
                              {sortBy === "brand" && (
                                <i
                                  className={`fas fa-sort-${sortOrder === "asc" ? "up" : "down"} ml-1`}
                                ></i>
                              )}
                              {sortBy !== "brand" && (
                                <i className="fas fa-sort ml-1 employee-sort-icon"></i>
                              )}
                            </th>

                            <th>Description</th>

                            <th
                              className="employee-sortable"
                              onClick={() => handleSort("purchaseDate")}
                            >
                              Purchase Date
                              {sortBy === "purchaseDate" && (
                                <i
                                  className={`fas fa-sort-${sortOrder === "asc" ? "up" : "down"} ml-1`}
                                ></i>
                              )}
                              {sortBy !== "purchaseDate" && (
                                <i className="fas fa-sort ml-1 employee-sort-icon"></i>
                              )}
                            </th>

                            <th
                              className="employee-sortable"
                              onClick={() => handleSort("warrantyExpiry")}
                            >
                              Warranty Expiry
                              {sortBy === "warrantyExpiry" && (
                                <i
                                  className={`fas fa-sort-${sortOrder === "asc" ? "up" : "down"} ml-1`}
                                ></i>
                              )}
                              {sortBy !== "warrantyExpiry" && (
                                <i className="fas fa-sort ml-1 employee-sort-icon"></i>
                              )}
                            </th>
                            <th>Allocation Status</th>

                            <th
                              className="employee-sortable"
                              onClick={() => handleSort("status")}
                            >
                              Status
                              {sortBy === "status" && (
                                <i
                                  className={`fas fa-sort-${sortOrder === "asc" ? "up" : "down"} ml-1`}
                                ></i>
                              )}
                              {sortBy !== "status" && (
                                <i className="fas fa-sort ml-1 employee-sort-icon"></i>
                              )}
                            </th>

                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {assets.length === 0 ? (
                            <tr>
                              <td colSpan="9" className="employee-empty">
                                No assets found
                              </td>
                            </tr>
                          ) : (
                            assets.map((asset, index) => {
                              const status = getAssetStatus(asset);

                              return (
                                <tr
                                  key={
                                    asset.id || `${asset.assetName}-${index}`
                                  }
                                >
                                  <td>
                                    <input
                                      type="checkbox"
                                      checked={selectedAssets.includes(
                                        asset.id,
                                      )}
                                      onChange={() =>
                                        handleSelectAsset(asset.id)
                                      }
                                    />
                                  </td>

                                  <td>{getSerialNumber(index)}</td>

                                  <td>{getAssetName(asset)}</td>

                                  <td>{asset.brand || "-"}</td>

                                  <td>{getDescription(asset)}</td>

                                  <td>{formatDate(asset.purchaseDate)}</td>

                                  <td>{formatDate(asset.warrantyExpiry)}</td>
                                  <td>
                                    {(() => {
                                      const latestAllocation =
                                        asset.allocation?.length > 0
                                          ? [...asset.allocation].sort(
                                              (a, b) =>
                                                new Date(
                                                  b.updatedAt || b.createdAt,
                                                ) -
                                                new Date(
                                                  a.updatedAt || a.createdAt,
                                                ),
                                            )[0]
                                          : null;

                                      return (
                                        <div style={{ textAlign: "center" }}>
                                          <span
                                            className={`employee-status ${
                                              latestAllocation?.status ===
                                              "Allocated"
                                                ? "employee-status-inactive"
                                                : "employee-status-active"
                                            }`}
                                          >
                                            {latestAllocation?.status ||
                                              "Unallocated"}
                                          </span>

                                          {latestAllocation?.employeeName && (
                                            <div
                                              style={{
                                                fontSize: "12px",
                                                marginTop: "4px",
                                                color: "#6c757d",
                                                fontWeight: "500",
                                                textAlign: "center",
                                              }}
                                            >
                                              {latestAllocation.employeeName}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })()}
                                  </td>

                                  <td>
                                    <span
                                      className={`employee-status employee-status-${getStatusClass(status)}`}
                                    >
                                      {status}
                                    </span>
                                  </td>

                                  <td>
                                    <div className="employee-actions">
                                      <button
                                        className="employee-action-btn employee-action-view"
                                        onClick={() => handleView(asset)}
                                        title="View"
                                      >
                                        <i className="fa-solid fa-eye"></i>
                                      </button>

                                      <button
                                        className="employee-action-btn employee-action-edit"
                                        onClick={() => handleEdit(asset)}
                                        title="Edit"
                                      >
                                        <i className="fa-solid fa-pencil"></i>
                                      </button>
                                      <button
                                        className="employee-action-btn employee-action-history"
                                        onClick={() => handleHistory(asset)}
                                        title="History"
                                      >
                                        <i className="fa-solid fa-clock-rotate-left"></i>
                                      </button>

                                      <button
                                        className="employee-action-btn employee-action-delete"
                                        onClick={() => handleDelete(asset)}
                                        title="Delete"
                                      >
                                        <i className="fa-solid fa-trash"></i>
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>

                    {assets.length > 0 && pagination.totalPages > 0 && (
                      <div className="employee-pagination">
                        <div
                          className="pagination-limit-container"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            marginRight: "auto",
                          }}
                        >
                          <label
                            htmlFor="paginationLimit"
                            style={{
                              margin: 0,
                              fontSize: "14px",
                              color: "#495057",
                            }}
                          >
                            Show:
                          </label>
                          <select
                            id="paginationLimit"
                            value={pagination.limit}
                            onChange={handleLimitChange}
                            style={{
                              padding: "6px 10px",
                              fontSize: "14px",
                              border: "1px solid #ced4da",
                              borderRadius: "4px",
                              backgroundColor: "white",
                              cursor: "pointer",
                              minWidth: "70px",
                            }}
                          >
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="75">75</option>
                            <option value="100">100</option>
                          </select>
                        </div>
                        <div style={{ display: "flex", gap: "5px" }}>
                          <button
                            className="pagination-btn"
                            onClick={() => handlePageChange(1)}
                            disabled={pagination.currentPage === 1}
                          >
                            «
                          </button>
                          <button
                            className="pagination-btn"
                            onClick={() =>
                              handlePageChange(pagination.currentPage - 1)
                            }
                            disabled={pagination.currentPage === 1}
                          >
                            ‹
                          </button>
                          {Array.from(
                            { length: pagination.totalPages },
                            (_, i) => i + 1,
                          ).map((page) => (
                            <button
                              key={page}
                              className={`pagination-btn ${pagination.currentPage === page ? "active" : ""}`}
                              onClick={() => handlePageChange(page)}
                            >
                              {page}
                            </button>
                          ))}
                          <button
                            className="pagination-btn"
                            onClick={() =>
                              handlePageChange(pagination.currentPage + 1)
                            }
                            disabled={
                              pagination.currentPage === pagination.totalPages
                            }
                          >
                            ›
                          </button>
                          <button
                            className="pagination-btn"
                            onClick={() =>
                              handlePageChange(pagination.totalPages)
                            }
                            disabled={
                              pagination.currentPage === pagination.totalPages
                            }
                          >
                            »
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {showActionModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowActionModal(false)}
        >
          <div className="modal-content1" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Asset Action</h3>

              <button
                className="modal-close"
                onClick={() => setShowActionModal(false)}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              <p>Selected Assets:</p>

              <div
                style={{
                  marginTop: "10px",
                  maxHeight: "180px",
                  overflowY: "auto",
                }}
              >
                {assets
                  .filter((asset) => selectedAssets.includes(asset.id))
                  .map((asset) => (
                    <p
                      key={asset.id}
                      style={{
                        marginBottom: "8px",
                        fontWeight: "500",
                      }}
                    >
                      • {asset.assetName || "-"}
                    </p>
                  ))}
              </div>
            </div>

            <div
              className="modal-footer"
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "10px",
                minHeight: "70px",
                alignItems: "center",
              }}
            >
              {loading ? (
                <Loading size={50} />
              ) : (
                <>
                  <button
                    className="btn-primary"
                    onClick={handleAssignAssets}
                    style={{
                      width: "120px",
                    }}
                  >
                    Allocate
                  </button>

                  <button
                    className="btn-primary"
                    onClick={handleUnallocateAssets}
                    style={{
                      width: "120px",
                    }}
                  >
                    Unallocate
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {showHistoryModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowHistoryModal(false)}
        >
          <div
            className="modal-content1"
            style={{ width: "900px", maxWidth: "95%" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>{selectedAssetName} - Asset History</h3>

              <button
                className="modal-close"
                onClick={() => setShowHistoryModal(false)}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div
              className="modal-body"
              style={{ maxHeight: "500px", overflowY: "auto" }}
            >
              <table className="employee-table">
                <thead>
                  <tr>
                    <th>SI No.</th>
                    <th>Asset Name</th>
                    <th>Brand</th>
                    <th>Allocated Date</th>
                    <th>Unallocated Date</th>
                  </tr>
                </thead>

                <tbody>
                  {selectedHistory.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="text-center">
                        No history found
                      </td>
                    </tr>
                  ) : (
                    selectedHistory.map((item, index) => (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td>{item.assetName || "-"}</td>
                        <td>{item.brand || "-"}</td>
                        <td>{formatDateTime(item.allocatedDate)}</td>
                        <td>{formatDateTime(item.unallocatedDate)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div className="modal-overlay" onClick={closeDeleteModal}>
          <div className="modal-content1" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Confirm Delete</h3>
              <button className="modal-close" onClick={closeDeleteModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <p>
                Are you sure you want to delete{" "}
                <strong>{getAssetName(assetToDelete)}</strong>?
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn-primary" onClick={confirmDelete}>
                Yes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StoreAssets;
